// FileSteps.js
// "The file" in the review dialog's file view (Spec 2 section 6.4, item 4): what
// Decode decided about the file as a whole, one collapsible card per process of
// the file's chain, in chain order:
//
//   1  Sheets and tables       (Segment, a workbook)  or
//      Tables in the document  (RecordSets, a document)
//   2  What to ingest          (Dispositions)
//   3  Date format             (DateOrder)
//   4  Date in the file name   (FileNameDate)
//   5  Shared values           (Pool: read-only, collapsed at first)
//
// Each card's header carries its step number, "{n} changes by you" when a
// person's versions are active, "updating…" while the step is about to rerun,
// and a "⋯" menu with "Start this step over" (every step but Pool). Its body ends
// with the step's notes on changes that no longer apply.
//
// Nothing here writes. Every change is handed to `onFileChange(run, options)`,
// which the dialog runs: it asks first when the change would un-approve the file
// (or, with `options.confirmation`, for any other reason), shows a refusal as a
// toast, and answers whether the change was made. Inline drafts (a header field,
// an instruction, a document value, the file-name date) stay local until their
// Save, Fix or Apply, and are cleared only once the change was made: a cancelled
// confirmation or a refusal leaves the draft as it was typed (00 N.4). While the
// file cannot be changed they are disabled, and kept unsent.
//
// A file refused at intake, or failed as too large, carries an empty record for
// every process: a step whose body reads a decision shows nothing for it.

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { activeDecisions, activeValue, activeVersion, isPending, personChangeCount } from '../../../utils/decisions';
import { editFileDecisions, requestFile, resetFileDecision, ignoreSheet, unignoreSheet, setDisposition } from '../../../services/cleaningGestures';
import { versionToken } from '../../../utils/blobPaths';
import { countNoun } from '../../../utils/pluralise';
import { formatTimestamp } from '../../../utils/timestamps';
import { regionColour } from './SheetViewer';
import { sourceLabel } from './UnitStepFrame';
import { UnitStatusIcon } from './ingestionStatusDisplay';

const SCALAR_PREFIX = 'scalar:';

const ORDER_LABEL = { day_first: 'Day / Month / Year', month_first: 'Month / Day / Year' };

// The chip that marks a decision a person made; Decode's own say nothing.
function YouChip({ version }) {
  if (version?.made_by !== 'person') return null;
  return (
    <Tooltip title={`Changed by you, ${formatTimestamp(version.made_at)}`}>
      <Chip size="small" color="primary" variant="outlined" label="You" sx={{ height: 20 }} />
    </Tooltip>
  );
}

// The "⋯" menu of a step.
function StepMenu({ items }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label="More"
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuList dense disablePadding>
          {items.map((item) => (
            <MenuItem
              key={item.label}
              onClick={() => {
                setAnchor(null);
                item.onClick();
              }}
            >
              <ListItemText primary={item.label} secondary={item.hint} />
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </>
  );
}

// One step's card: header, collapsible body, and the step's dropped notes.
// `describeKey(key)` names a decision key for those notes.
function ProcessCard({ index, title, record, menuItems, startOpen = true, describeKey, children }) {
  const [open, setOpen] = useState(startOpen);
  const changes = personChangeCount(record);
  const pending = isPending(record);
  return (
    <Paper variant="outlined" sx={{ mb: 1.5 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer' }}
      >
        <Box sx={{
          width: 22, height: 22, borderRadius: '50%', bgcolor: 'action.selected',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600,
        }}
        >
          {index}
        </Box>
        <Typography variant="subtitle2">{title}</Typography>
        {changes > 0 && (
          <Chip size="small" color="primary" variant="outlined" label={`${countNoun(changes, 'change')} by you`} />
        )}
        {pending && (
          <Chip
            size="small"
            color="info"
            icon={<CircularProgress size={12} color="inherit" />}
            label={record.requested === 'start_over' ? 'starting over…' : 'updating…'}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {menuItems.length > 0 && <StepMenu items={menuItems} />}
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {children}
          {record.dropped.map((note) => (
            <Box key={`${JSON.stringify(note.key)}-${formatTimestamp(note.at)}`} sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
              <WarningIcon color="warning" sx={{ fontSize: 16, mt: '2px' }} />
              <Typography variant="caption" color="text.secondary">
                Your change to {describeKey(note.key)} no longer applies: {note.reason}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Sheets and tables (Segment) ──────────────────────────────────────────────

// A header field shown as a chip, edited in place. `onSave(text)` answers whether the
// change was made; the editor closes only then.
function HeaderFieldChip({ fieldKey, value, editable, onSave }) {
  const [draft, setDraft] = useState(null);
  if (draft === null) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={<span>{fieldKey}: <b>{value}</b></span>}
        onClick={editable ? () => setDraft(String(value ?? '')) : undefined}
        title={editable ? 'Click to change' : undefined}
      />
    );
  }
  const save = async () => {
    if (await onSave(draft)) setDraft(null);
  };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2">{fieldKey}:</Typography>
      <TextField
        size="small"
        autoFocus
        value={draft}
        disabled={!editable}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter' && editable) save(); }}
        inputProps={{ 'aria-label': `${fieldKey} value` }}
        sx={{ width: 160 }}
      />
      <Button size="small" variant="contained" disabled={!editable} onClick={save}>Save</Button>
      <Button size="small" onClick={() => setDraft(null)}>Cancel</Button>
    </Box>
  );
}

function SegmentBody({ companyId, tableId, file, units, editBlock, onFileChange, onOpenSheet }) {
  const record = file.processes.segment;
  const editable = !editBlock;
  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        The tables and header fields Decode found on each sheet. Open a sheet to adjust them.
      </Typography>
      {activeDecisions(record).map(({ key: sheetName, version }) => {
        const plan = version.value;
        const fieldEntries = Object.entries(plan.fields);
        const sheetUnits = units.filter((unit) => unit.source.sheet === sheetName);
        let contents;
        if (plan.regions.length > 0) contents = <Typography variant="caption" color="text.secondary">{countNoun(plan.regions.length, 'table')}</Typography>;
        else if (fieldEntries.length > 0) contents = <Typography variant="caption" color="text.secondary">header fields only</Typography>;
        else contents = <Chip size="small" color="warning" label="nothing found" />;
        return (
          <Box key={sheetName} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={700}>{sheetName}</Typography>
              <YouChip version={version} />
              {contents}
              <Box sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" disabled={!editable} onClick={() => onOpenSheet(sheetName)}>
                Open sheet
              </Button>
              {version.made_by === 'person' && (
                <Button
                  size="small"
                  disabled={!editable}
                  onClick={() => onFileChange(() => resetFileDecision(companyId, tableId, file.id, 'segment', sheetName))}
                >
                  Undo my changes
                </Button>
              )}
            </Box>
            {plan.regions.map((region, index) => {
              const unit = sheetUnits.find((candidate) => candidate.source.region_id === region.region_id);
              const leftOut = region.exclude_rows.length;
              return (
                <Box key={region.region_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: 2, py: 0.25 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: regionColour(index) }} />
                  <Typography variant="body2" fontWeight={600}>Table {index + 1}</Typography>
                  {region.name && <Typography variant="body2">· {region.name}</Typography>}
                  <Typography variant="caption" color="text.secondary">
                    rows {region.body_first_row}–{region.body_last_row}, columns {region.col_first}–{region.col_last}
                    {leftOut > 0 ? `, ${leftOut} left out` : ''}
                  </Typography>
                  {Object.entries(region.section_values).map(([name, sectionValue]) => (
                    <Chip key={name} size="small" variant="outlined" label={`${name}: ${sectionValue}`} />
                  ))}
                  {unit && unit.disposition === 'ignore' && <Chip size="small" variant="outlined" label="ignored" />}
                </Box>
              );
            })}
            {fieldEntries.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pl: 2, pt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Header fields:</Typography>
                {fieldEntries.map(([fieldKey, fieldValue]) => (
                  <HeaderFieldChip
                    key={fieldKey}
                    fieldKey={fieldKey}
                    value={fieldValue}
                    editable={editable}
                    // The sheet's plan is saved whole, so the save names the version it was
                    // made from and is refused if someone else changed the plan meanwhile.
                    onSave={(draft) => onFileChange(() => editFileDecisions(
                      companyId, tableId, file.id, 'segment',
                      [{ key: sheetName, value: { ...plan, fields: { ...plan.fields, [fieldKey]: draft } } }],
                      { expectedVersions: { [sheetName]: version.made_at } },
                    ))}
                  />
                ))}
              </Box>
            )}
            {plan.regions.length === 0 && fieldEntries.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 2 }}>
                Decode could not read this sheet. Open it to mark its tables, or ignore it.
              </Typography>
            )}
          </Box>
        );
      })}
    </>
  );
}

// ── Tables in the document (RecordSets) ──────────────────────────────────────

function RecordSetsBody({ companyId, tableId, file, editBlock, onFileChange }) {
  const record = file.processes.record_sets;
  const editable = !editBlock;
  // Local drafts: an instruction being typed, per set; a document value being
  // changed, per value. Nothing is written until Fix or Save.
  const [instructionDrafts, setInstructionDrafts] = useState({});
  const [scalarDrafts, setScalarDrafts] = useState({});

  const decisions = activeDecisions(record);
  if (decisions.length === 0) {
    return <Typography variant="caption" color="text.secondary">The document could not be read.</Typography>;
  }
  const sets = decisions.filter(({ key }) => !String(key).startsWith(SCALAR_PREFIX));
  const scalars = decisions.filter(({ key }) => String(key).startsWith(SCALAR_PREFIX));

  // A set's whole value is replaced, so the save names the version it was made
  // from and is refused if someone else changed the set meanwhile.
  const replaceSet = (setKey, version, value) => onFileChange(() => editFileDecisions(
    companyId, tableId, file.id, 'record_sets', [{ key: setKey, value }],
    { expectedVersions: { [setKey]: version.made_at } },
  ));

  // The instruction typed for a set, added to it. The field is cleared once the
  // change was made.
  const addInstruction = async (setKey, version, promote) => {
    const correction = {
      id: versionToken(),
      text: (instructionDrafts[setKey] || '').trim(),
      promote,
      // The writer's clock, stored as a timestamp: a server timestamp cannot be
      // written inside a list, and the instruction keeps this moment for good.
      created_at: new Date(),
    };
    const made = await replaceSet(setKey, version, {
      ...version.value, corrections: [...version.value.corrections, correction],
    });
    if (made) setInstructionDrafts((previous) => ({ ...previous, [setKey]: '' }));
  };

  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        The tables Decode read from the pages. If something is wrong, tell it what to change and it reads the pages again.
      </Typography>
      {sets.map(({ key: setKey, version }) => {
        const definition = version.value.definition;
        const corrections = version.value.corrections;
        const draft = instructionDrafts[setKey] || '';
        return (
          <Box key={setKey} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={700}>{definition.name}</Typography>
              <YouChip version={version} />
              <Typography variant="caption" color="text.secondary">
                pages {definition.page_first}–{definition.page_last} · {definition.grain}
              </Typography>
              <Box sx={{ flex: 1 }} />
              {version.made_by === 'person' && (
                <Button
                  size="small"
                  disabled={!editable}
                  onClick={() => onFileChange(() => resetFileDecision(companyId, tableId, file.id, 'record_sets', setKey))}
                >
                  Undo my changes
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pl: 2, pt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Columns:</Typography>
              {definition.fields.map((field) => <Chip key={field.name} size="small" variant="outlined" label={field.name} />)}
            </Box>
            {corrections.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pl: 2, pt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Your instructions:</Typography>
                {corrections.map((correction) => (
                  <Chip
                    key={correction.id}
                    size="small"
                    variant="outlined"
                    title={correction.promote
                      ? 'Remembered for future uploads from this profile. Click the pin to forget it after this file.'
                      : 'This file only. Click the pin to remember it for future uploads.'}
                    icon={(
                      <IconButton
                        size="small"
                        aria-label={correction.promote ? 'Forget after this file' : 'Remember for future uploads'}
                        disabled={!editable}
                        onClick={() => replaceSet(setKey, version, {
                          ...version.value,
                          corrections: corrections.map((entry) => (
                            entry.id === correction.id ? { ...entry, promote: !entry.promote } : entry)),
                        })}
                        sx={{ p: 0 }}
                      >
                        {correction.promote
                          ? <PushPinIcon sx={{ fontSize: 14 }} color="warning" />
                          : <PushPinOutlinedIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    )}
                    label={correction.text}
                    onDelete={editable ? () => replaceSet(setKey, version, {
                      ...version.value,
                      corrections: corrections.filter((entry) => entry.id !== correction.id),
                    }) : undefined}
                    deleteIcon={<CloseIcon aria-label="Remove" />}
                  />
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2, pt: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder='Tell Decode what to change, for example "The paid amount is the third figure, not the second"'
                value={draft}
                disabled={!editable}
                onChange={(event) => setInstructionDrafts((previous) => ({ ...previous, [setKey]: event.target.value }))}
                inputProps={{ 'aria-label': `Instruction for ${definition.name}` }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={!editable || !draft.trim()}
                onClick={() => addInstruction(setKey, version, false)}
              >
                Fix
              </Button>
              <Tooltip title="Also applied to future uploads from this profile">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!editable || !draft.trim()}
                    onClick={() => addInstruction(setKey, version, true)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Fix and remember
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        );
      })}
      {scalars.length > 0 && (
        <Box sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>Values read from the document</Typography>
          {scalars.map(({ key: scalarKey, version }) => {
            const draft = scalarDrafts[scalarKey];
            const changed = draft !== undefined && draft !== String(version.value.value ?? '');
            return (
              <Box key={scalarKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.25 }}>
                <Typography variant="body2" sx={{ minWidth: 150 }}>{scalarKey.slice(SCALAR_PREFIX.length)}</Typography>
                <TextField
                  size="small"
                  value={draft ?? String(version.value.value ?? '')}
                  disabled={!editable}
                  onChange={(event) => setScalarDrafts((previous) => ({ ...previous, [scalarKey]: event.target.value }))}
                  inputProps={{ 'aria-label': `${scalarKey.slice(SCALAR_PREFIX.length)} value` }}
                  sx={{ width: 180 }}
                />
                <Typography variant="caption" color="text.secondary">page {version.value.page}</Typography>
                <YouChip version={version} />
                {changed && (
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!editable}
                    onClick={async () => {
                      if (!await replaceSet(scalarKey, version, { ...version.value, value: draft })) return;
                      // The value saved, the field follows the document again.
                      setScalarDrafts((previous) => {
                        const next = { ...previous };
                        delete next[scalarKey];
                        return next;
                      });
                    }}
                  >
                    Save
                  </Button>
                )}
                {version.made_by === 'person' && !changed && (
                  <Button
                    size="small"
                    disabled={!editable}
                    onClick={() => onFileChange(() => resetFileDecision(companyId, tableId, file.id, 'record_sets', scalarKey))}
                  >
                    Undo
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
}

// ── What to ingest (Dispositions) ────────────────────────────────────────────

function DispositionsBody({ companyId, tableId, file, units, profile, editBlock, onFileChange }) {
  const record = file.processes.dispositions;
  const editable = !editBlock;
  // The layouts the file's profile has learned to ignore: a person's ignore of one table
  // teaches its layout, while a whole-sheet ignore teaches nothing (Spec 2 section 3.4).
  // The caption after a person's ignore of a region says so only for a learned layout.
  const ignoredLayouts = profile?.disposition_signatures?.ignored || [];
  // Units grouped under their sheet, in the file's order; a document's units have no sheet.
  const groups = [];
  units.forEach((unit) => {
    const sheet = unit.source.sheet || '';
    let group = groups.find((candidate) => candidate.sheet === sheet);
    if (!group) {
      group = { sheet, units: [] };
      groups.push(group);
    }
    group.units.push(unit);
  });

  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        What to do with each table: ingest it, use it as header values for the other sheets, or leave it out.
      </Typography>
      {groups.map((group) => {
        const allIgnored = group.units.every((unit) => unit.disposition === 'ignore');
        return (
          <Box key={group.sheet || 'document'} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
            {group.sheet && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={700}>{group.sheet}</Typography>
                <Box sx={{ flex: 1 }} />
                {allIgnored ? (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!editable}
                    onClick={() => onFileChange(() => unignoreSheet(companyId, tableId, file.id, group.sheet))}
                  >
                    Use this sheet again
                  </Button>
                ) : (
                  <Button
                    size="small"
                    color="error"
                    disabled={!editable}
                    onClick={() => onFileChange(() => ignoreSheet(companyId, tableId, file.id, group.sheet))}
                  >
                    Ignore this sheet
                  </Button>
                )}
              </Box>
            )}
            {group.units.map((unit) => {
              const version = activeVersion(record, unit.id);
              // The decision shows the person's choice at once; the unit's own copy
              // follows when the file's chain has run. Until the file's listener
              // delivers the decision of a unit it has just created, the copy is shown.
              const disposition = version ? version.value : unit.disposition;
              const learnedIgnore = disposition === 'ignore' && version?.made_by === 'person'
                && Boolean(unit.source.region_id) && ignoredLayouts.includes(unit.signature);
              const baseRows = Object.values(unit.column_stats)[0]?.rows ?? unit.sample_rows.length;
              return (
                <Box key={unit.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: group.sheet ? 2 : 0, py: 0.5 }}>
                  <UnitStatusIcon status={unit.disposition === 'ingest' ? unit.status : 'Ignored'} disposition={unit.disposition} />
                  <Typography variant="body2" sx={{ minWidth: 160 }}>{unit.name}</Typography>
                  <Select
                    size="small"
                    value={disposition}
                    disabled={!editable}
                    onChange={(event) => onFileChange(
                      () => setDisposition(companyId, tableId, file.id, unit.id, event.target.value),
                    )}
                    inputProps={{ 'aria-label': `What to do with ${unit.name}` }}
                    sx={{ minWidth: 220 }}
                  >
                    <MenuItem value="ingest">Ingest</MenuItem>
                    <MenuItem value="context" disabled={!unit.single_row}>
                      Header values for the other sheets{unit.single_row ? '' : ' (one row only)'}
                    </MenuItem>
                    <MenuItem value="ignore">Ignore</MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary">
                    {countNoun(baseRows, 'row')}
                    {learnedIgnore ? ' · Decode will ignore this layout in future uploads' : ''}
                  </Typography>
                  <YouChip version={version} />
                </Box>
              );
            })}
          </Box>
        );
      })}
    </>
  );
}

// ── Date format (DateOrder) ──────────────────────────────────────────────────

function DateOrderBody({ companyId, tableId, file, units, editBlock, onFileChange }) {
  const record = file.processes.date_order;
  const editable = !editBlock;
  const fileVersion = activeVersion(record, 'file');
  if (fileVersion === null) return null;
  // Proven: the dates themselves show the order, and no choice can overrule them.
  const proven = Boolean(fileVersion.produced?.proven);
  const ingestUnits = units.filter((unit) => unit.disposition === 'ingest');
  // A unit's own decision is empty while it reads its dates as the file does.
  const exceptions = ingestUnits.filter((unit) => activeValue(record, unit.id));
  let caption = 'Assumed: no date in this file shows which. Switch it if 03/04/2026 is the 4th of March.';
  if (proven) caption = 'The dates in this file show which';
  else if (fileVersion.made_by === 'person') caption = 'Your choice';

  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        How dates like 03/04/2026 are read.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ minWidth: 110 }}>Whole file</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={fileVersion.value}
          disabled={proven || !editable}
          onChange={(event, order) => {
            if (!order || order === fileVersion.value) return;
            onFileChange(() => editFileDecisions(
              companyId, tableId, file.id, 'date_order', [{ key: 'file', value: order }], {},
            ));
          }}
        >
          <ToggleButton value="day_first" sx={{ textTransform: 'none' }}>{ORDER_LABEL.day_first}</ToggleButton>
          <ToggleButton value="month_first" sx={{ textTransform: 'none' }}>{ORDER_LABEL.month_first}</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">{caption}</Typography>
        <YouChip version={fileVersion} />
      </Box>
      {exceptions.map((unit) => {
        const own = activeVersion(record, unit.id);
        return (
          <Box key={unit.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2, pt: 0.5 }}>
            <Typography variant="body2" sx={{ minWidth: 160 }}>{unit.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {ORDER_LABEL[own.value]}
              {own.produced?.proven ? ' · its own dates show it' : ''}
            </Typography>
            <YouChip version={own} />
            {own.made_by === 'person' && (
              <Button
                size="small"
                disabled={!editable}
                onClick={() => onFileChange(() => resetFileDecision(companyId, tableId, file.id, 'date_order', unit.id))}
              >
                Use automatic date format
              </Button>
            )}
          </Box>
        );
      })}
      {ingestUnits.length > 1 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 2, pt: 0.5 }}>
          One sheet reads its dates differently? Change it on that table, under Dates and numbers.
        </Typography>
      )}
    </>
  );
}

// ── Date in the file name (FileNameDate) ─────────────────────────────────────

function FileNameDateBody({ companyId, tableId, file, editBlock, onFileChange }) {
  const record = file.processes.file_name_date;
  const editable = !editBlock;
  const version = activeVersion(record, 'value');
  // The date being typed; null while the person has not changed it.
  const [draft, setDraft] = useState(null);
  if (version === null) return null;
  const stored = version.value || '';
  const changed = draft !== null && draft !== stored;
  let caption = 'the name holds no date';
  if (version.value) caption = version.made_by === 'person' ? 'set by you' : `read from '${file.name}'`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ minWidth: 150 }}>Date in the file name</Typography>
      <TextField
        type="date"
        size="small"
        value={draft ?? stored}
        disabled={!editable}
        onChange={(event) => setDraft(event.target.value)}
        inputProps={{ 'aria-label': 'Date in the file name' }}
      />
      <Typography variant="caption" color="text.secondary">{caption}</Typography>
      <YouChip version={version} />
      {changed && (
        <Button
          size="small"
          variant="contained"
          disabled={!editable}
          onClick={async () => {
            const made = await onFileChange(() => editFileDecisions(
              companyId, tableId, file.id, 'file_name_date', [{ key: 'value', value: draft || null }], {},
            ));
            if (made) setDraft(null);
          }}
        >
          Apply
        </Button>
      )}
      {version.made_by === 'person' && !changed && (
        <Button
          size="small"
          disabled={!editable}
          onClick={() => onFileChange(() => resetFileDecision(companyId, tableId, file.id, 'file_name_date', 'value'))}
        >
          Undo
        </Button>
      )}
    </Box>
  );
}

// ── Shared values (Pool) ─────────────────────────────────────────────────────

function PoolBody({ file, units, tableName }) {
  const entries = Object.entries(file.pool).map(([reference, entry]) => {
    // Where the value came from: the file's name, the document, or a sheet's unit.
    let from;
    if (entry.source === 'file_name') from = 'the file name';
    else if (entry.source === 'document') from = 'the document';
    else from = `the ${units.find((unit) => unit.id === entry.source)?.name || entry.source} sheet`;
    return { reference, value: entry.value, from };
  });
  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Values from the file&apos;s header sheets and its name. Any column of {tableName} can be filled from one of them.
      </Typography>
      {entries.length === 0 ? (
        <Typography variant="caption" color="text.secondary">None in this file.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell />
              <TableCell>From</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map(({ reference, value, from }) => (
              <TableRow key={reference}>
                <TableCell>{sourceLabel(reference)}</TableCell>
                <TableCell>{String(value ?? '')}</TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{from}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

// ── The chain ────────────────────────────────────────────────────────────────

function FileSteps({ companyId, tableId, file, units, profile, tableName, editBlock, onFileChange, onOpenSheet }) {
  if (Object.keys(file.processes).length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Reading the file. Nothing to review yet.</Typography>
      </Box>
    );
  }

  const unitName = (key) => units.find((unit) => unit.id === key)?.name;
  // The words a dropped note uses for a decision's key.
  const describeKey = (processId) => (key) => {
    if (processId === 'date_order') return key === 'file' ? "the file's date format" : `the date format of ${unitName(key) || 'a table'}`;
    if (processId === 'file_name_date') return 'the date in the file name';
    if (processId === 'dispositions') return unitName(key) || 'a table';
    if (processId === 'record_sets' && String(key).startsWith(SCALAR_PREFIX)) return String(key).slice(SCALAR_PREFIX.length);
    if (processId === 'record_sets') return activeValue(file.processes.record_sets, key)?.definition.name || 'a table';
    return typeof key === 'string' ? key : JSON.stringify(key);
  };
  const props = { companyId, tableId, file, units, editBlock, onFileChange };

  // "Redo this step" is not offered: every decision of these steps is kept across
  // runs, so a rerun would change nothing.
  const menuFor = (processId, options = {}) => (editBlock ? [] : [{
    label: 'Start this step over',
    hint: 'Decode decides from scratch; your changes here are forgotten',
    onClick: () => onFileChange(
      () => requestFile(companyId, tableId, file.id, processId, 'start_over'),
      options,
    ),
  }]);

  const firstStep = file.type === 'pdf'
    ? {
      id: 'record_sets',
      title: 'Tables in the document',
      body: <RecordSetsBody {...props} />,
      // Starting over re-reads and re-extracts the whole document at full cost.
      menu: menuFor('record_sets', {
        confirmation: `Decode will read all ${file.assets.page_count} pages again and extract every table again, `
          + 'and your instructions on this document will be discarded. Continue?',
      }),
    }
    : {
      id: 'segment',
      title: 'Sheets and tables',
      body: <SegmentBody {...props} onOpenSheet={onOpenSheet} />,
      menu: menuFor('segment'),
    };
  const chain = [
    firstStep,
    { id: 'dispositions', title: 'What to ingest', body: <DispositionsBody {...props} profile={profile} />, menu: menuFor('dispositions') },
    { id: 'date_order', title: 'Date format', body: <DateOrderBody {...props} />, menu: menuFor('date_order') },
    { id: 'file_name_date', title: 'Date in the file name', body: <FileNameDateBody {...props} />, menu: menuFor('file_name_date') },
    { id: 'pool', title: 'Shared values', body: <PoolBody file={file} units={units} tableName={tableName} />, menu: [], startOpen: false },
  ];

  return (
    <>
      {chain.map((step, position) => (file.processes[step.id] ? (
        <ProcessCard
          key={step.id}
          index={position + 1}
          title={step.title}
          record={file.processes[step.id]}
          menuItems={step.menu}
          startOpen={step.startOpen !== false}
          describeKey={describeKey(step.id)}
        >
          {step.body}
        </ProcessCard>
      ) : null))}
    </>
  );
}

export default FileSteps;
